import { useState } from 'react';
import type { EconSectorCategory, EconSectorResponse } from '../api/chartDataTypes';
import { buildEconSectorSummarySentence } from '../utils/econSectorFormat';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { SectorSelector } from './SectorSelector';

type EconSectorChartPanelProps = {
    response: EconSectorResponse;
};

const PROMPT_SUMMARY = 'Select a sector to hear its weighting.';
const EMPTY_SUMMARY = 'No sector data available.';

/**
 * Economic Sector Representation orchestrator. Unlike Perf10k / ShoutTna there
 * is no value to summarize on first render — the data is a point-in-time
 * snapshot keyed by sector name, not date. The shell's banner therefore opens
 * with a prompt ("Select a sector to hear its weighting."); the user presses
 * Tab to reach the Sector dropdown, matching the behaviour of the other chart
 * types so the shell's assertive intro is not preempted by an auto-focus.
 */
export function EconSectorChartPanel({ response }: EconSectorChartPanelProps) {
    const { security, title, report_date, data } = response;
    const { categories } = data;

    const [selectedCategory, setSelectedCategory] = useState<EconSectorCategory | null>(null);

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const hasCategories = categories.length > 0;

    let summary: string;
    let summaryHTML: string | undefined;
    if (!hasCategories) {
        summary = EMPTY_SUMMARY;
    } else if (selectedCategory === null) {
        summary = PROMPT_SUMMARY;
    } else {
        const parts = buildEconSectorSummarySentence(selectedCategory);
        summary = parts.text;
        summaryHTML = parts.html;
    }

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}>
            {hasCategories ? (
                <SectorSelector
                    categories={categories}
                    idPrefix='econ_sector'
                    onSelectedCategoryChange={setSelectedCategory}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
